import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Article, ArticleSource, ArticleFAQ } from '@/types/articles';

export interface ArticleEditorFormState {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  focusKeyword: string;
  metaTitle: string;
  metaDescription: string;
  status: Article['status'];
  categoryIds: string[];
  tagIds: string[];
  featuredImageUrl: string;
  featuredImageAlt: string;
  schemaType: Article['schema_type'];
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  canonicalUrl: string;
  isCornerstone: boolean;
  noindex: boolean;
  authorId: string | null;
  coAuthorIds: string[];
  expertReviewerIds: string[];
  sources: ArticleSource[];
  faqs: ArticleFAQ[];
}

const initialFormState: ArticleEditorFormState = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  focusKeyword: '',
  metaTitle: '',
  metaDescription: '',
  status: 'draft',
  categoryIds: [],
  tagIds: [],
  featuredImageUrl: '',
  featuredImageAlt: '',
  schemaType: 'Article',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  twitterTitle: '',
  twitterDescription: '',
  twitterImage: '',
  canonicalUrl: '',
  isCornerstone: false,
  noindex: false,
  authorId: null,
  coAuthorIds: [],
  expertReviewerIds: [],
  sources: [],
  faqs: [],
};

interface ArticleEditorContextType {
  isOpen: boolean;
  articleId: string | null;
  isNew: boolean;
  formState: ArticleEditorFormState;
  activeTab: string;
  setFormState: React.Dispatch<React.SetStateAction<ArticleEditorFormState>>;
  setActiveTab: (tab: string) => void;
  openEditor: (articleId?: string) => void;
  closeEditor: () => void;
  resetEditor: () => void;
  updateFormField: <K extends keyof ArticleEditorFormState>(
    field: K,
    value: ArticleEditorFormState[K]
  ) => void;
}

const ArticleEditorContext = createContext<ArticleEditorContextType | undefined>(undefined);

export function ArticleEditorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [formState, setFormState] = useState<ArticleEditorFormState>(initialFormState);
  const [activeTab, setActiveTab] = useState('general');

  const isNew = !articleId;

  const openEditor = useCallback((id?: string) => {
    if (id) {
      setArticleId(id);
    } else {
      if (!isOpen) {
        setArticleId(null);
        setFormState(initialFormState);
      } else {
        setArticleId(null);
      }
    }
    setIsOpen(true);
  }, [isOpen]);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetEditor = useCallback(() => {
    setFormState(initialFormState);
    setArticleId(null);
    setActiveTab('general');
  }, []);

  const updateFormField = useCallback(<K extends keyof ArticleEditorFormState>(
    field: K,
    value: ArticleEditorFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <ArticleEditorContext.Provider
      value={{
        isOpen,
        articleId,
        isNew,
        formState,
        activeTab,
        setFormState,
        setActiveTab,
        openEditor,
        closeEditor,
        resetEditor,
        updateFormField,
      }}
    >
      {children}
    </ArticleEditorContext.Provider>
  );
}

export function useArticleEditor() {
  const context = useContext(ArticleEditorContext);
  if (!context) {
    throw new Error('useArticleEditor must be used within ArticleEditorProvider');
  }
  return context;
}
